import { startMeasurement } from "@/utils/benchmark"
import { store } from "@/store/store"
import { loadingStateAtom } from "@/store/app-state"
import { photosAtom, sortedPhotosAtom } from "@/store/photo/photos-state"
import { getImageInfo, getImageInfoRecords, updateImageInfo } from "@/store/processing/image-info"
import {
	getDeepClusterDistance,
	isImageInfoManuallyUpdated,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { generateClusters, reAssignKeyPhotoIfNeeded } from "@/lib/ai/ai-cluster"
import { isFinalAdjustmentsUpdated, prepareForApplyUpdatedAIStyle } from "@/lib/ai/ai-style"
import { CatalogManager, ImageInfoRecord, PhotoRecord, StyleInfoRecord } from "@/lib/catalog"
import { batchApplyStyle } from "@/store/style/apply-style"
import {
	currentStyleAtom,
	referenceImagesAtomFamily,
	saveAsCurrentStyle,
	styleReferencesAtom,
} from "./style-state"
import { forwardProcessingAtom } from "../settings-state"
import { logAiInfo } from "@/lib/ai/utils"
import { getPresetsAndReferencesMapFromMarkedRefs } from "./create-style"
import {
	sceneDThreshForVerySimilarNeighbor,
	shouldUseOwnAutoLight,
} from "@/lib/ai/calibration/ai-calibration-keyadj"
import { StyleService } from "./style-service"
import { projectAtom } from "../project/projects-state"
import {
	createStyleReferenceForLocalPhoto,
	deleteStyleReference,
	updateStyleReference,
} from "@/lib/catalog/helpers"
import { hasReferenceWebpData256x256Source } from "@/lib/style/reference-webp-data"
import { holdAIStyleScheduling } from "../processing/ai-style-performance"

export async function updateAIStyle(style: StyleInfoRecord, triggerSource?: string | null) {
	const release = holdAIStyleScheduling()
	try {
		await updateAIStyleAndQueuePhotos(style, triggerSource)
	} finally {
		release()
	}
}

async function updateAIStyleAndQueuePhotos(style: StyleInfoRecord, triggerSource?: string | null) {
	const project = store.get(projectAtom)
	if (!project) {
		console.warn("No project found when updating AI style")
		return
	}
	const endMeasurement = startMeasurement("updateAIProfile")

	store.set(loadingStateAtom, { status: "updatingProfile" })

	//Update AI Profile
	const photos = store.get(photosAtom)
	const currentReferences = store.get(styleReferencesAtom)
	const currentImages = store.get(referenceImagesAtomFamily(false)) //All references including implicit
	const newImages: Record<string, ImageInfoRecord> = {}
	const removedImages: Record<string, ImageInfoRecord> = {}

	//Processing existing reference images
	for (const ref of currentReferences) {
		const localImageId = ref.imageInfo.imageId
		// Note: photos[imageId] can be undefined for references in different projects
		// Note: we should not remove implicit references even though they are not marked as references by users
		if (photos[localImageId] && photos[localImageId]?.kind !== "reference" && !ref.isImplicit) {
			//removed from approved
			const currentImage = currentImages[localImageId]
			if (currentImage) {
				removedImages[localImageId] = currentImage
			}
			delete currentImages[localImageId]
			await deleteStyleReference(ref.imageId, style.styleId)
		}
	}

	//Go through approved images
	const approvedPhotos = Object.values(photos).filter((photo) => photo.kind == "reference")
	for (const photo of approvedPhotos) {
		const info = getImageInfo(photo.id)
		if (!info) {
			continue
		}

		const existingInfo = currentImages[photo.id]
		if (existingInfo && !isImageInfoManuallyUpdated(existingInfo, info)) {
			//existing ref image with no change
			continue
		}

		if (existingInfo) {
			await updateStyleReference(info, style.styleId, false)
		} else {
			const newReferenceRecord = createStyleReferenceForLocalPhoto(info, style.styleId, false)
			await CatalogManager.storeStyleReference(newReferenceRecord)
		}
		newImages[photo.id] = info
	}

	const references = await CatalogManager.fetchStyleReferences(style.styleId)
	store.set(styleReferencesAtom, references)

	const { stylePresets } = await getPresetsAndReferencesMapFromMarkedRefs({
		photos: Object.keys(newImages).map((id) => photos[id] as PhotoRecord),
		existingPresetsMap: { ...style.presets },
		existingReferencesMap: { ...currentImages },
	})
	logAiInfo({ newImages, removedImages })

	//Reprocess as needed
	if (Object.keys(newImages).length > 0 || Object.keys(removedImages).length > 0) {
		const referenceImages = { ...currentImages, ...newImages }
		const newStyle = {
			...style,
			updatedAt: new Date(),
			presets: { ...style.presets, ...stylePresets },
			referenceCount: Object.keys(referenceImages).length,
			prioritizeImageIds: [], // clear prioritizeImageIds after updating style to avoid affecting future updates
		}
		console.log("Old style preset number:", Object.keys(style.presets).length)
		console.log("New style preset number:", Object.keys(newStyle.presets).length)

		const { reprocessPhotos, processPhotos } = updateImageInfosAndGetProcessList(
			newStyle,
			newImages,
			removedImages,
			referenceImages,
		)
		let photosToProcess = reprocessPhotos.concat(processPhotos)

		if (store.get(forwardProcessingAtom)) {
			photosToProcess = getPhotosAfterUpdatedReferences({
				photosToProcess,
				newImages,
				removedImages,
				includeSameClusterPhotos: true,
			})
		}

		batchApplyStyle(photosToProcess, newStyle, false, triggerSource)

		await saveAsCurrentStyle(newStyle)

		if (import.meta.env.DEV) {
			const noAfterImageRefs = Object.values(referenceImages).filter(
				(imageInfo) => !hasReferenceWebpData256x256Source(imageInfo),
			)
			if (noAfterImageRefs.length > 0) {
				console.warn(
					`UpdateAIStyle: ${noAfterImageRefs.length} refs missing webpdata source`,
					noAfterImageRefs.map((r) => r.imageId),
				)
			}
		}

		logAiInfo("[updateAIStyle]", { reprocessPhotos, processPhotos, photosToProcess, newStyle })
		console.log(`Updated AI style:`, { refCount: Object.keys(referenceImages).length })
	}
	store.set(loadingStateAtom, { status: null })

	endMeasurement()
}

function getPhotosAfterUpdatedReferences(options: {
	photosToProcess: PhotoRecord[]
	newImages: Record<string, ImageInfoRecord>
	removedImages: Record<string, ImageInfoRecord>
	includeSameClusterPhotos: boolean
}) {
	const { photosToProcess, newImages, removedImages, includeSameClusterPhotos } = options
	const sortedPhotos = store.get(sortedPhotosAtom)

	const photoIdToIndex: Record<string, number> = Object.fromEntries(
		sortedPhotos.map((photo, index) => [photo.id, index]),
	)

	const updatedRefs = { ...newImages, ...removedImages }
	const updatedRefIds = Object.keys(updatedRefs)
	const updatedRefIndices = updatedRefIds.map((id) => photoIdToIndex[id] ?? -1)
	const validIndices = updatedRefIndices.filter((val) => val >= 0)
	const minIndex = validIndices.length > 0 ? Math.min(...validIndices) : -1

	const photos = photosToProcess.filter((photo) => (photoIdToIndex[photo.id] ?? -1) > minIndex)

	if (includeSameClusterPhotos) {
		const minRef = sortedPhotos[minIndex]
		const minRefInfo = updatedRefs[minRef?.id ?? ""]

		if (minRefInfo) {
			const keyId = minRefInfo.keyNeighborId ?? minRefInfo.imageId
			const allImageInfos = getImageInfoRecords()
			const members = Array.from(allImageInfos.values()).filter(
				(info) => info.keyNeighborId === keyId,
			)
			for (const member of members) {
				const photo = photosToProcess.find((photo) => photo.id === member.imageId)
				if (photo && photos.indexOf(photo) === -1) {
					photos.push(photo)
				}
			}
		}
	}

	return photos
}

function updateImageInfosAndGetProcessList(
	newStyle: StyleInfoRecord,
	newImages: Record<string, ImageInfoRecord>,
	removedImages: Record<string, ImageInfoRecord>,
	referenceImages: Record<string, ImageInfoRecord>,
) {
	const photos = store.get(photosAtom)
	const reprocessPhotos: PhotoRecord[] = []
	const processPhotos: PhotoRecord[] = []

	// generate clusters
	const clusters = generateClusters(photos)

	logAiInfo({ clusters })

	for (const entry of Object.entries(clusters)) {
		let keyPhotoID = entry[0]
		const memberIDs = entry[1]

		// Need to first process the key image in a cluster
		let keyPhoto = photos[keyPhotoID]
		if (!keyPhoto) {
			continue
		}

		// Just a safe guard: if key photo is locked or hidden, reassign it;
		// but the handling should already be done with ensurePhotoNotKeyNeighbor() during hide/lock photo.
		keyPhotoID = reAssignKeyPhotoIfNeeded(keyPhoto, memberIDs)
		keyPhoto = photos[keyPhotoID]
		if (!keyPhoto) {
			continue
		}

		const { needsProcess, needsReprocess } = checkReprocessAndUpdateKeyInfo({
			keyPhoto,
			newImages,
			removedImages,
			referenceImages,
		})
		if (needsProcess) {
			processPhotos.push(keyPhoto)
		} else if (needsReprocess) {
			reprocessPhotos.push(keyPhoto)
		}
		const isKeyPhotoRef = keyPhoto.kind === "reference"

		// then process other members
		const updatedMemberIDs: string[] = []
		for (const id of memberIDs) {
			if (id === keyPhotoID) {
				continue
			}

			const photo = photos[id]
			if (!photo) {
				continue
			}

			const { needsProcess, needsReprocess } = checkReprocessAndUpdateMemberInfo({
				photo,
				newImages,
				style: newStyle,
				isKeyPhotoRef,
				updatedMemberIDs,
			})
			if (needsProcess) {
				processPhotos.push(photo)
			} else if (needsReprocess) {
				reprocessPhotos.push(photo)
			}
			updatedMemberIDs.push(id)
		}
	}

	return { reprocessPhotos, processPhotos }
}

function checkReprocessAndUpdateKeyInfo(options: {
	keyPhoto: PhotoRecord
	newImages: Record<string, ImageInfoRecord>
	removedImages: Record<string, ImageInfoRecord>
	referenceImages: Record<string, ImageInfoRecord>
}) {
	const { keyPhoto, newImages, removedImages, referenceImages } = options
	let needsProcess = false
	let needsReprocess = false

	const keyInfo = getImageInfo(keyPhoto.id)

	// Need to update imageInfo for locked files for later use (e.g. unlock), just don't add it to queue.
	if (keyInfo && keyPhoto.kind != "reference" && keyInfo.aiStyleAdjustments) {
		const updatedInfo = prepareForApplyUpdatedAIStyle({
			imageInfo: keyInfo,
			referenceImages,
			newRefPhotos: newImages,
			removedRefPhotos: removedImages,
		})

		updateImageInfo(updatedInfo)

		if (keyPhoto.kind != "locked" && !keyPhoto.isHidden && updatedInfo.needApplyStyle) {
			needsReprocess = true
		}
	} else if (keyPhoto.kind != "reference" && keyPhoto.kind != "locked" && !keyPhoto.isHidden) {
		needsProcess = true
	}

	return { needsProcess, needsReprocess }
}

function checkReprocessAndUpdateMemberInfo(options: {
	photo: PhotoRecord
	newImages: Record<string, ImageInfoRecord>
	style: StyleInfoRecord
	isKeyPhotoRef: boolean
	updatedMemberIDs: string[]
}) {
	const { photo, newImages, style, isKeyPhotoRef, updatedMemberIDs } = options
	let needsProcess = false
	let needsReprocess = false
	const imageInfo = getImageInfo(photo.id)

	if (photo.kind != "reference" && imageInfo && imageInfo.aiStyleAdjustments) {
		//Processed before
		const keyPhotoID = imageInfo.keyNeighborId ?? imageInfo.imageId
		const keyInfo = getImageInfo(keyPhotoID)

		if (!keyInfo) {
			return { needsProcess, needsReprocess }
		}

		// just set updated information from the keyNeighbor's info
		const prevPresetID = imageInfo.presetId
		const updatedInfo: ImageInfoRecord = {
			...imageInfo,
			nearestReferences: keyInfo.nearestReferences,
			presetId: keyInfo.presetId,
			biases: keyInfo.biases,
		}
		// only if the key photo updated, change the status; otherwise, keep the old status which could be true
		// because it's possible that this image hasn't been processed during last queue before queue cancellation
		if (prevPresetID !== keyInfo.presetId || (keyInfo.needApplyStyle && !isKeyPhotoRef)) {
			updatedInfo.needApplyStyle = true
		} else if (newImages[keyPhotoID]) {
			// if the key photo is newly approved (marked as reference) and would change the member's final output
			const updated = isFinalAdjustmentsUpdated({
				photo,
				imageInfo,
				updatedInfo,
				keyInfo: newImages[keyPhotoID] ?? keyInfo,
				style,
			})
			if (updated) updatedInfo.needApplyStyle = true
		}

		if (updatedInfo.features.useOwnAutoLightMap?.[keyInfo.imageId] == undefined) {
			updatedInfo.features.useOwnAutoLightMap = getUseOwnAutoLightMap(
				updatedInfo,
				keyInfo,
				updatedMemberIDs,
			)
		}

		updateImageInfo(updatedInfo)

		if (photo.kind != "locked" && !photo.isHidden && updatedInfo.needApplyStyle) {
			needsReprocess = true
		}

		logAiInfo("Checked member reprocessing for", photo.id, { updatedInfo, needsReprocess })
	} else if (photo.kind != "reference" && photo.kind != "locked" && !photo.isHidden) {
		needsProcess = true
	}

	return { needsProcess, needsReprocess }
}

function getUseOwnAutoLightMap(
	updatedInfo: ImageInfoRecord,
	keyInfo: ImageInfoRecord,
	updatedMemberIDs: string[],
) {
	const useOwnAutoLightMap: Record<string, boolean> =
		updatedInfo.features.useOwnAutoLightMap ?? {}

	// Decide whether to use own auto light params in key adjustments, refer to very similar neighbor
	let neighbor: string | null | undefined = null
	if (updatedMemberIDs.length > 0) {
		neighbor = updatedInfo.features.verySimilarNeighbor
		// find very similar neighbor in updatedMemberIDs
		if (!neighbor || !updatedMemberIDs.includes(neighbor)) {
			for (const id of updatedMemberIDs) {
				const info = getImageInfo(id)
				if (!info) continue
				const dScene = getDeepClusterDistance(updatedInfo, info)
				if (dScene < sceneDThreshForVerySimilarNeighbor) {
					neighbor = id
					break
				}
			}
		}
	}
	const neighborFlag = neighbor
		? getImageInfo(neighbor)?.features.useOwnAutoLightMap?.[keyInfo.imageId]
		: undefined
	if (neighborFlag != undefined) {
		useOwnAutoLightMap[keyInfo.imageId] = neighborFlag
	} else {
		useOwnAutoLightMap[keyInfo.imageId] = shouldUseOwnAutoLight({
			imageInfo: updatedInfo,
			keyImageInfo: keyInfo,
		})
	}

	return useOwnAutoLightMap
}

export async function updateAIStyleName(style: StyleInfoRecord, newName: string) {
	const updatedStyle: StyleInfoRecord = {
		...style,
		name: newName,
		updatedAt: new Date(),
	}

	await StyleService.updateStyle(updatedStyle)

	const currentStyle = store.get(currentStyleAtom)
	if (currentStyle && currentStyle.styleId === style.styleId) {
		const updatedCurrentStyle = {
			...currentStyle,
			...updatedStyle,
		}
		store.set(currentStyleAtom, updatedCurrentStyle)
		await CatalogManager.updateStyleInfo(style.styleId, updatedCurrentStyle)
	}
}

export async function updateAIStyleCover(style: StyleInfoRecord, newCover: Blob) {
	const coverImageUrl = await StyleService.uploadStyleReferenceImage(newCover)
	if (coverImageUrl) {
		const updatedStyle: StyleInfoRecord = {
			...style,
			coverImageUrl: coverImageUrl,
			updatedAt: new Date(),
		}

		const currentStyle = store.get(currentStyleAtom)
		if (currentStyle && currentStyle.styleId === style.styleId) {
			const updatedCurrentStyle = {
				...currentStyle,
				...updatedStyle,
			}
			store.set(currentStyleAtom, updatedCurrentStyle)
			await CatalogManager.updateStyleInfo(style.styleId, updatedCurrentStyle)
		}

		await StyleService.updateStyle(updatedStyle, {
			styleId: style.styleId,
			coverImageUrl: URL.createObjectURL(newCover),
		})
	}

	return coverImageUrl
}
